import type {
  MRT_ColumnFiltersState,
  MRT_ColumnPinningState,
  MRT_ColumnSizingState,
  MRT_SortingState,
} from 'material-react-table';
import { formatLocalDate, getLastFullMonth } from '../../lib/dateUtils';

// Re-export for convenience
export { getLastFullMonth };

export type DensitySetting = 'compact' | 'comfortable' | 'spacious';

// export type ProviderSource = 'opentrack' | 'dealer_vault';
export type ProviderSource = 'opentrack';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ReportFiltersState {
  typeCodeFilter: string[];
  lineTypeFilter: string[];
  linePayFilter: string[];
  coveredLineFilter: string[];
  columnFilters: MRT_ColumnFiltersState;
}

export interface SavedViewState {
  name: string;
  description?: string;
  date?: DateRange;
  sorting: MRT_SortingState;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columnSizing: MRT_ColumnSizingState;
  columnPinning: MRT_ColumnPinningState;
  columnFilters: MRT_ColumnFiltersState;
  density: DensitySetting;
  compactHeaders: boolean;
  zebraRows: boolean;
  globalFilter: string;
  typeCodeFilter: string[];
  lineTypeFilter: string[];
  linePayFilter: string[];
  coveredLineFilter: string[];
  pageSize: number;
  providerSource?: ProviderSource;
}

export type SavedViewPayload = Omit<SavedViewState, 'name' | 'description'>;

const DEFAULT_TYPE_CODES = ['I'] as const;
const DEFAULT_LINE_TYPES = ['', 'L'] as const;
const DEFAULT_LINE_PAY = ['C', ''] as const;
const DEFAULT_COVERED_LINE = ['true'] as const;

const DEFAULT_COLUMN_FILTERS: MRT_ColumnFiltersState = [
  { id: 'TransCode', value: ['CP', ''] },
] as const;

const FINANCIAL_COLUMNS = ['Serv Sales', 'Serv Prem', 'Parts Sales', 'Parts Premium'];
const FINANCIAL_ANCHOR = 'T1 Label';

const cloneColumnFilter = (filter: MRT_ColumnFiltersState[number]) => {
  if (!filter) return filter;
  const value = Array.isArray(filter.value) ? [...filter.value] : filter.value;
  return { ...filter, value };
};

export function cloneColumnFilters(filters: MRT_ColumnFiltersState): MRT_ColumnFiltersState {
  return filters.map(cloneColumnFilter);
}

export function createFilterState(overrides?: Partial<ReportFiltersState>): ReportFiltersState {
  return {
    typeCodeFilter: overrides?.typeCodeFilter
      ? [...overrides.typeCodeFilter]
      : [...DEFAULT_TYPE_CODES],
    lineTypeFilter: overrides?.lineTypeFilter
      ? [...overrides.lineTypeFilter]
      : [...DEFAULT_LINE_TYPES],
    linePayFilter: overrides?.linePayFilter ? [...overrides.linePayFilter] : [...DEFAULT_LINE_PAY],
    coveredLineFilter: overrides?.coveredLineFilter
      ? [...overrides.coveredLineFilter]
      : [...DEFAULT_COVERED_LINE],
    columnFilters: overrides?.columnFilters
      ? cloneColumnFilters(overrides.columnFilters)
      : cloneColumnFilters(DEFAULT_COLUMN_FILTERS as MRT_ColumnFiltersState),
  };
}

export function applyOpenTrackPreset(prev: ReportFiltersState): ReportFiltersState {
  const preset = createFilterState();
  const matches =
    arraysEqual(prev.typeCodeFilter, preset.typeCodeFilter) &&
    arraysEqual(prev.lineTypeFilter, preset.lineTypeFilter) &&
    arraysEqual(prev.linePayFilter, preset.linePayFilter) &&
    arraysEqual(prev.coveredLineFilter, preset.coveredLineFilter) &&
    columnFiltersEqual(prev.columnFilters, preset.columnFilters);

  return matches ? prev : preset;
}

export function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function columnFiltersEqual(a: MRT_ColumnFiltersState, b: MRT_ColumnFiltersState): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (!left || !right) return false;
    if (left.id !== right.id) return false;
    const leftVal = left.value;
    const rightVal = right.value;
    const leftArray = Array.isArray(leftVal) ? leftVal : [leftVal];
    const rightArray = Array.isArray(rightVal) ? rightVal : [rightVal];
    if (!arraysEqual(leftArray.map(String), rightArray.map(String))) {
      return false;
    }
  }
  return true;
}

export function sanitizeColumnFilters(filters: MRT_ColumnFiltersState): MRT_ColumnFiltersState {
  return filters.filter((filter) => filter?.id !== 'providerSource').map(cloneColumnFilter);
}

export function buildViewPayload(args: {
  date: DateRange;
  sorting: MRT_SortingState;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columnSizing: MRT_ColumnSizingState;
  columnPinning: MRT_ColumnPinningState;
  density: DensitySetting;
  compactHeaders: boolean;
  zebraRows: boolean;
  globalFilter: string;
  filters: ReportFiltersState;
  pageSize: number;
  providerSource?: ProviderSource;
}): SavedViewPayload {
  return {
    date: args.date,
    sorting: args.sorting,
    columnVisibility: args.columnVisibility,
    columnOrder: args.columnOrder,
    columnSizing: args.columnSizing,
    columnPinning: args.columnPinning,
    columnFilters: sanitizeColumnFilters(args.filters.columnFilters),
    density: args.density,
    compactHeaders: args.compactHeaders,
    zebraRows: args.zebraRows,
    globalFilter: args.globalFilter,
    typeCodeFilter: [...args.filters.typeCodeFilter],
    lineTypeFilter: [...args.filters.lineTypeFilter],
    linePayFilter: [...args.filters.linePayFilter],
    coveredLineFilter: [...args.filters.coveredLineFilter],
    pageSize: args.pageSize,
    providerSource: args.providerSource,
  };
}

export function getRollingDateRange(days = 30): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - Math.max(0, days - 1));
  return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
}

const densityValues = new Set<DensitySetting>(['compact', 'comfortable', 'spacious']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isDateRange = (value: unknown): value is DateRange =>
  isRecord(value) && typeof value.startDate === 'string' && typeof value.endDate === 'string';

const toStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  const filtered = value.filter((item): item is string => typeof item === 'string');
  return filtered.length ? filtered.slice() : null;
};

const sanitizeBooleanRecord = (value: unknown): Record<string, boolean> | null => {
  if (!isRecord(value)) return null;
  const entries = Object.entries(value).filter(([, v]) => typeof v === 'boolean') as Array<
    [string, boolean]
  >;
  if (!entries.length) return null;
  return Object.fromEntries(entries);
};

const sanitizeNumberRecord = (value: unknown): Record<string, number> | null => {
  if (!isRecord(value)) return null;
  const entries = Object.entries(value).filter(
    ([, v]) => typeof v === 'number' && Number.isFinite(v),
  ) as Array<[string, number]>;
  if (!entries.length) return null;
  return Object.fromEntries(entries);
};

const sanitizePinningState = (value: unknown): MRT_ColumnPinningState | null => {
  if (!isRecord(value)) return null;
  const left = toStringArray(value.left);
  const right = toStringArray(value.right);
  const result: MRT_ColumnPinningState = {};
  if (left) result.left = left;
  if (right) result.right = right;
  return Object.keys(result).length ? result : null;
};

const sanitizeSortingState = (value: unknown): MRT_SortingState | null => {
  if (!Array.isArray(value)) return null;
  const result: MRT_SortingState = [];
  value.forEach((entry) => {
    if (!isRecord(entry) || typeof entry.id !== 'string') return;
    if ('desc' in entry && typeof entry.desc !== 'boolean') return;
    // desc is either boolean (if present) or defaults to false
    const desc = 'desc' in entry ? (entry.desc as boolean) : false;
    result.push({ id: entry.id, desc });
  });
  return result.length ? result : null;
};

const sanitizeColumnFiltersState = (value: unknown): MRT_ColumnFiltersState | null => {
  if (!Array.isArray(value)) return null;
  const result: MRT_ColumnFiltersState = [];
  value.forEach((entry) => {
    if (!isRecord(entry) || typeof entry.id !== 'string') return;
    // Explicitly type-cast after validation to satisfy TypeScript
    result.push({ id: entry.id, value: entry.value } as { id: string; value: unknown });
  });
  return result.length ? result : null;
};

/**
 * Validates and normalizes a saved view configuration from any source
 * (localStorage, server API, templates, etc.) to ensure type safety
 * and prevent corrupted data from breaking the application.
 *
 * Returns null if config is completely invalid, or a sanitized
 * Partial<SavedViewState> with only valid fields included.
 */
export function normalizeSavedViewConfig(config: unknown): Partial<SavedViewState> | null {
  if (!isRecord(config)) return null;
  const normalized: Partial<SavedViewState> = {};
  let hasValue = false;

  if (isDateRange(config.date)) {
    normalized.date = { ...config.date };
    hasValue = true;
  }

  const sorting = sanitizeSortingState(config.sorting);
  if (sorting) {
    normalized.sorting = sorting;
    hasValue = true;
  }

  const columnVisibility = sanitizeBooleanRecord(config.columnVisibility);
  if (columnVisibility) {
    normalized.columnVisibility = columnVisibility;
    hasValue = true;
  }

  const order = toStringArray(config.columnOrder);
  if (order) {
    normalized.columnOrder = order;
    hasValue = true;
  }

  const columnSizing = sanitizeNumberRecord(config.columnSizing);
  if (columnSizing) {
    normalized.columnSizing = columnSizing;
    hasValue = true;
  }

  const columnPinning = sanitizePinningState(config.columnPinning);
  if (columnPinning) {
    normalized.columnPinning = columnPinning;
    hasValue = true;
  }

  const columnFilters = sanitizeColumnFiltersState(config.columnFilters);
  if (columnFilters) {
    normalized.columnFilters = cloneColumnFilters(columnFilters);
    hasValue = true;
  }

  if (typeof config.density === 'string' && densityValues.has(config.density as DensitySetting)) {
    normalized.density = config.density as DensitySetting;
    hasValue = true;
  }

  if (typeof config.compactHeaders === 'boolean') {
    normalized.compactHeaders = config.compactHeaders;
    hasValue = true;
  }

  if (typeof config.zebraRows === 'boolean') {
    normalized.zebraRows = config.zebraRows;
    hasValue = true;
  }

  if (typeof config.globalFilter === 'string') {
    normalized.globalFilter = config.globalFilter;
    hasValue = true;
  }

  const typeCodes = toStringArray(config.typeCodeFilter);
  if (typeCodes) {
    normalized.typeCodeFilter = typeCodes;
    hasValue = true;
  }

  const lineTypes = toStringArray(config.lineTypeFilter);
  if (lineTypes) {
    normalized.lineTypeFilter = lineTypes;
    hasValue = true;
  }

  const linePay = toStringArray(config.linePayFilter);
  if (linePay) {
    normalized.linePayFilter = linePay;
    hasValue = true;
  }

  const covered = toStringArray(config.coveredLineFilter);
  if (covered) {
    normalized.coveredLineFilter = covered;
    hasValue = true;
  }

  if (typeof config.pageSize === 'number' && Number.isFinite(config.pageSize)) {
    normalized.pageSize = config.pageSize;
    hasValue = true;
  }

  if (
    typeof config.providerSource === 'string' &&
    ['both', 'opentrack', 'dealer_vault'].includes(config.providerSource)
  ) {
    normalized.providerSource = config.providerSource as ProviderSource;
    hasValue = true;
  }

  return hasValue ? normalized : null;
}

/**
 * Type guard to validate if an unknown value is a valid SavedViewState config.
 * Use this before applying configs to catch structural issues early.
 */
export function isValidSavedViewConfig(config: unknown): config is Partial<SavedViewState> {
  if (!isRecord(config) || Array.isArray(config)) return false;

  // If providerSource is present, it must be valid
  if (
    config.providerSource !== undefined &&
    (typeof config.providerSource !== 'string' ||
      !['opentrack', 'dealer_vault', 'both'].includes(config.providerSource))
  ) {
    return false;
  }

  // If density is present, it must be valid
  if (
    config.density !== undefined &&
    (typeof config.density !== 'string' || !densityValues.has(config.density as DensitySetting))
  ) {
    return false;
  }

  // If columnFilters is present, it must be an array
  if (config.columnFilters !== undefined && !Array.isArray(config.columnFilters)) {
    return false;
  }

  // If pageSize is present, it must be a positive finite number
  if (
    config.pageSize !== undefined &&
    (typeof config.pageSize !== 'number' ||
      !Number.isFinite(config.pageSize) ||
      config.pageSize <= 0)
  ) {
    return false;
  }

  // If boolean fields are present, they must be booleans
  if (config.compactHeaders !== undefined && typeof config.compactHeaders !== 'boolean') {
    return false;
  }

  if (config.zebraRows !== undefined && typeof config.zebraRows !== 'boolean') {
    return false;
  }

  return true;
}

/**
 * Older saved column orders placed financial columns before technical fields.
 * This guard ensures those legacy orders get reset so new templates render
 * with the intended right-aligned finance columns.
 */
function shouldMigrateFinancialColumns(order: string[]): boolean {
  const positions = FINANCIAL_COLUMNS.map((col) => order.indexOf(col)).filter((idx) => idx !== -1);
  if (positions.length === 0) return false;
  const anchorIndex = order.indexOf(FINANCIAL_ANCHOR);
  if (anchorIndex !== -1 && positions.some((pos) => pos < anchorIndex)) {
    return true;
  }
  for (let i = 1; i < positions.length; i += 1) {
    if (positions[i] - positions[i - 1] > 1) {
      return true;
    }
  }
  return false;
}

export function mergeColumnOrder(
  saved: unknown,
  defaultOrder: string[],
): { order: string[]; didChange: boolean } {
  const savedOrder = Array.isArray(saved)
    ? saved.filter((value): value is string => typeof value === 'string' && value.length > 0)
    : [];

  if (savedOrder.length === 0) {
    return { order: [...defaultOrder], didChange: false };
  }

  if (shouldMigrateFinancialColumns(savedOrder)) {
    return { order: [...defaultOrder], didChange: true };
  }

  const next = [...savedOrder];
  let changed = false;

  defaultOrder.forEach((columnId, index) => {
    if (next.includes(columnId)) return;

    let insertIndex = next.length;
    for (let j = index + 1; j < defaultOrder.length; j += 1) {
      const nextColumnId = defaultOrder[j];
      const existingIndex = next.indexOf(nextColumnId);
      if (existingIndex !== -1) {
        insertIndex = existingIndex;
        break;
      }
    }

    next.splice(insertIndex, 0, columnId);
    changed = true;
  });

  return { order: next, didChange: changed };
}

/**
 * Removes duplicate saved views based on name, keeping the last occurrence.
 * Helps prevent localStorage corruption from duplicate entries.
 */
export function deduplicateViews(views: SavedViewState[]): SavedViewState[] {
  const seen = new Set<string>();
  const result: SavedViewState[] = [];

  // Process in reverse to keep the last occurrence
  for (let i = views.length - 1; i >= 0; i -= 1) {
    const view = views[i];
    if (!view?.name || seen.has(view.name)) {
      continue;
    }
    seen.add(view.name);
    result.unshift(view);
  }

  return result;
}

/**
 * Limits the number of saved views to prevent localStorage quota issues.
 * Keeps the most recently used views (last N entries).
 */
export function pruneOldViews(views: SavedViewState[], maxViews = 50): SavedViewState[] {
  if (views.length <= maxViews) return views;
  return views.slice(views.length - maxViews);
}
